/**
 * Types for the reusable watermark library (VE-24693).
 *
 * A `WatermarkDefinition` is one saved, named watermark (text or image) stored
 * as an SDO. When applied to a document it is COPIED into an `AppliedWatermark`
 * snapshot (see documents/models), so editing/deleting a definition never
 * changes documents already using it (AC#3).
 *
 * A definition carries what it IS — name, type, and either the text and its
 * color or an image — plus the DEFAULTS a fresh application starts from
 * (`size`, `grayscale`, `opacity`).
 *
 * The distinction between a default and a live value is the whole of AC#3, so
 * it is worth stating precisely: these are read ONCE, by
 * {@link appliedFromDefinition}, to seed a new `AppliedWatermark`. Nothing reads
 * them back afterwards. Editing a definition's default size therefore changes
 * what the NEXT application looks like and leaves every document already using
 * it untouched — which is the property that lets a library entry be edited at
 * all. A field that documents already in flight would read is a different kind
 * of field, and does not belong here.
 *
 * The definition used to carry `fontSize`, `layout`, `position` and `scale`
 * as live values; nothing read them back, and two call sites copied different
 * subsets, so the same library entry came out at a different size depending on
 * which surface applied it. `size` below is deliberately NOT that: it is a
 * seed, with one reader.
 *
 * Position and rotation stay off the definition entirely. They are meaningless
 * until there is a page to place a mark on, and the operator sets them by
 * dragging on the canvas (AC#7).
 */
import {
  AppliedWatermark,
  createImageWatermark,
  createTextWatermark,
  MAX_WATERMARK_TEXT_LENGTH,
  WatermarkType,
} from '@redact-modules/documents/models';
import {
  clampWatermarkInset,
  clampWatermarkRotation,
  clampWatermarkScale,
  clampWatermarkText,
  fontScaleOf,
  WatermarkAnchorH,
  WatermarkAnchorV,
  WatermarkTextAlign,
} from '@redact-modules/documents/watermarkTextMetrics';

/** The JSON `data` payload stored on each watermark SDO record. */
export interface WatermarkDefinitionData {
  /** User-facing name (AC#2). */
  watermarkName: string;
  type: WatermarkType;
  // text
  text?: string;
  /** Hex only — see {@link normalizeWatermarkColor} for why. */
  colorHex?: string;
  // image
  /**
   * PNG data URL of the rasterized upload, in its ORIGINAL COLOURS — the colour
   * base an applied watermark derives grayscale and tint from.
   *
   * **Normally absent since §K.** The raster lives on the watermark's TDO as a
   * `watermark-raster` asset; this field is written only when the
   * {@link STORE_WATERMARK_RASTER_IN_SDO} revert switch is on. It is still READ
   * unconditionally, and preferred when present, which is what lets rows from
   * either shape resolve through one path (`resolveWatermarkRaster`) and what
   * makes flipping that switch a deploy rather than a migration.
   *
   * **No longer a property of the published schema.** It was dropped from the
   * schema body once no row carried it (`docs/schemas/WatermarkLibrarySchema.md`).
   * Reading and writing the key both still work — `additionalProperties` is open,
   * so the value round-trips — but nothing validates it server-side any more, and
   * `sanitizeStoredImageDataUrl` on the read path is the only thing holding it to
   * a PNG. Turning the revert switch on in earnest means putting the property
   * back first; the likelier outcome is that this field and its inline path are
   * stripped out entirely.
   *
   * There used to be an `imageDataUrl` beside this, "the image as drawn", and
   * before that as the ONLY image field. By the end it was a verified exact
   * duplicate — the only writer assigned the same raster to both, and a
   * definition has no colour control that could make them differ — so it was a
   * third of the library payload, downloaded by every user on every session,
   * carrying nothing. Both it and its read path in `vetRecord` are gone (B1);
   * the rows that depended on it were on the first dev registry and have been
   * removed.
   */
  imageBaseDataUrl?: string;
  /**
   * Container TDO holding this watermark's artwork (§K): a
   * `watermark-source-image` asset with the upload's own bytes, and a
   * `watermark-raster` asset with our re-encoded PNG.
   *
   * Present on every image definition written since §K. Absent means the row
   * predates it (or was written with {@link STORE_WATERMARK_RASTER_IN_SDO} on
   * and nothing else), and the reader falls back to the inline
   * `imageBaseDataUrl` — see `resolveWatermarkRaster`.
   *
   * The TDO is created BEFORE this row and deleted AFTER it, so the only
   * inconsistency either failure can produce is an orphan TDO: invisible, and
   * nothing a user can see is broken.
   */
  imageTdoId?: string;
  /**
   * Small inline PNG of the artwork — 256px longest edge, aspect preserved.
   *
   * The ONLY image form that stays on the SDO row, and what every surface that
   * merely lists watermarks draws: the settings table, the editor picker, the
   * batch picker, the drawer. The full raster is fetched from
   * {@link imageTdoId} only when a mark is actually applied.
   *
   * That split is what makes the library cheap to list. The list is fetched on
   * three separate route entries with no cross-route cache, so what a row costs
   * is paid on every navigation — kilobytes now rather than the whole artwork.
   */
  previewDataUrl?: string;
  /**
   * Original filename WITH its original extension, e.g. `logo.png`.
   *
   * Display and download-name only, and NOT trusted for either: it is a free
   * string on an org-writable record. Download forces the extension from the
   * media type it actually validated rather than believing this.
   */
  imageFileName?: string;
  /**
   * Rasterized height / width, captured at upload so consumers can lay the
   * image out without decoding it. Optional: definitions saved before this
   * field existed fall back to 1.
   */
  imageAspect?: number;
  /**
   * DEFAULT fill opacity, seeded onto a new application. Absent falls back to
   * the factory default for the type.
   *
   * Optional like every other default here. It was required only because it
   * predates them — a fact about the order things were built in rather than
   * about the data — and a required key is the one kind that cannot be added or
   * removed without migrating every row.
   *
   * The cap lives in code (`clampWatermarkOpacity`), not in the schema: it is a
   * product rule we expect to tune, and a published schema is the most
   * expensive place to keep one.
   */
  opacity?: number;

  // --- defaults seeded onto a new application (see the header) -------------
  /**
   * Which media a watermark may be applied to.
   *
   * THREE BOOLEANS rather than one array, unlike a redaction status's
   * `statusTypes`. The SDO filter matches scalars exactly — `PICKABLE_FILTER`
   * in `services/index.ts` already relies on that, with string values because a
   * boolean literal 500s the endpoint — and nothing in this app has ever
   * filtered an SDO on array membership. So "give me the watermarks that apply
   * to documents" is one server-side filter with these and a client-side pass
   * over the whole library with an array.
   *
   * No `audio`: a watermark is something you SEE, and there is nothing to stamp
   * it onto in an audio file. The vocabulary is deliberately not the statuses
   * one for that reason.
   *
   * STORED BUT NOT SURFACED. No control sets them today. Documents default to
   * TRUE and the other two to FALSE, because documents is the only one
   * implemented — offering a watermark for a video that nothing will ever stamp
   * is worse than not offering it.
   *
   * `stampWriteDefaults` writes all three on every save, so an absent key means
   * "not written by this app" rather than "the operator chose false" — see
   * {@link watermarkAppliesTo} for how that is read.
   *
   * Read CLIENT-side only, by `selectPickableWatermarks`. They are deliberately
   * not in the query's `PICKABLE_FILTER`: an SDO filter matches on the stored
   * value, so `appliesToDocuments: true` would drop every row written before
   * the key existed — the absent-key rule below is exactly what a server-side
   * filter cannot express. The three booleans replaced an array so that a
   * future filter CAN be written per kind; until a row without the key is
   * impossible, that filter stays here.
   */
  appliesToDocuments?: boolean;
  appliesToImages?: boolean;
  appliesToVideos?: boolean;
  /**
   * Default rotation of the applied mark, in degrees. Seeds `rotation`.
   * Absent reads as 0 — flat, which is what a mark has always started at.
   */
  rotation?: number;
  /**
   * Default "Position on Screen" — which point of the page a fresh application
   * is pinned to. Seeds `anchorH`/`anchorV`. Absent reads as centre/middle,
   * which is where every mark landed before these existed.
   *
   * A DEFAULT like the rest of this block: dragging the mark in the editor
   * switches it to free placement and the anchor stops applying, and editing
   * this never moves a mark already on a document.
   */
  anchorH?: WatermarkAnchorH;
  anchorV?: WatermarkAnchorV;
  /**
   * Default "Inset from edge", a fraction of the SMALLER page dimension. Seeds
   * `inset`. Only edge anchors use it — an inset from centre means nothing.
   */
  inset?: number;
  /**
   * Default custom position, as a fraction of page width / height — set by
   * dragging the mark in the settings preview. Seeds `customXPct`/`customYPct`.
   *
   * Page-RELATIVE on purpose: a definition is applied to documents whose size
   * it cannot know, so a stored point would land somewhere different on every
   * one of them. When set it overrides `anchorH`/`anchorV`/`inset`, which stay
   * on the record so returning to a grid position restores what it was.
   */
  customXPct?: number;
  customYPct?: number;
  /**
   * Default alignment of the LINES within a multi-line text block. Seeds
   * `align`. Absent reads as centre; does nothing on a single line.
   */
  align?: WatermarkTextAlign;
  /**
   * Default for "draw the text in bold". Seeds `bold`. Absent reads as FALSE,
   * like every other flag here.
   *
   * It used to read as true, because text was drawn bold on both sides before
   * the toggle existed and absent-as-false would have restyled every row
   * written before it. That inversion is gone: bold is still what a NEW
   * watermark starts as, but the form writes it explicitly rather than leaving
   * it to be inferred from a missing key.
   */
  bold?: boolean;
  /**
   * Stamp this watermark on any document the FIRST time it is opened.
   *
   * "First time" means the document has never been saved — read from the
   * absence of a `document-redaction-data` asset, which the editor already
   * loads for its "Last saved" indicator. Once a document has been saved even
   * once, its own watermarks win and nothing is auto-applied to it again, so
   * this can never overwrite an operator's decision; it can only furnish the
   * document they have not made one about yet.
   *
   * See `documents/sagas/watermarkAutoApply.ts`.
   */
  autoApply?: boolean;
  /**
   * Default width of an IMAGE definition, as a fraction of page width (0..1).
   * Seeds `scale` on the applied instance.
   */
  imageScale?: number;
  /**
   * Default size of a TEXT definition, as a coefficient of page width applied
   * to the FONT SIZE: `fontSizePt = fontScale x pageWidthPt`. Seeds `fontScale`
   * on the applied instance.
   *
   * Two keys rather than one shared "size", deliberately. Both are fractions of
   * page width, and they multiply different things — an image's WIDTH against
   * text's FONT SIZE — so a single key would be a standing invitation to
   * conflate them, and conflating them is what the old model did: `size` meant
   * "width of the whole string" for text, with the font size back-derived by
   * dividing by character count. One setting rendered "OK" at 306pt and a
   * 34-character line at 18pt.
   */
  fontScale?: number;
  /**
   * Default for "render in grayscale". Optional; absent reads as false.
   *
   * Same name as the applied instance's flag (documents/models), which is the
   * point: it is one idea and it was called `blackAndWhite` in both places
   * until C2/E3 renamed them together. No read of the old name survives — the
   * feature is pre-release and the rows that had it have been removed.
   */
  grayscale?: boolean;
  /**
   * Default for "force white to transparent" — **RESERVED, nothing reads it.**
   *
   * An upload with no alpha channel (a JPEG, or a PNG flattened onto white)
   * stamps a visible white box on anything that is not a white page. The fix is
   * a knockout applied where grayscale and tint already are: luminance mapped to
   * alpha, in `deriveWatermarkImage`, BEFORE tint — tint first turns white into
   * a solid block of the tint colour.
   *
   * Declared now because it costs nothing: `additionalProperties` is permissive,
   * so an added optional key needs no republish either way, and having the name
   * settled keeps the next person from inventing a second one. Absent reads as
   * false, like every other flag here. See §H2 in
   * `plans/VE-24693-watermark-data-model-revisions.md` for the implementation
   * notes and the rejected alternatives.
   *
   * Worth knowing before it is built: it removes white EVERYWHERE, including
   * white lettering inside a dark seal, so it wants to be a visible toggle
   * rather than something applied silently.
   */
  removeWhiteBackground?: boolean;

  // --- library housekeeping ------------------------------------------------
  /**
   * Whether the watermark is offered in the editor and batch pickers. Absent
   * reads as ENABLED, so rows written before this field existed keep showing.
   */
  enabled?: boolean;
  /**
   * Position in the settings list. Reserved now so drag-to-reorder can be added
   * without a schema migration; nothing sorts by it yet.
   */
  displayOrder?: number;
  /**
   * Soft delete. `true` hides the row from the settings table and from every
   * picker, and frees its name for reuse, while documents that already applied
   * it are unaffected — they hold their own snapshot (AC#3), which is exactly
   * why a soft delete is safe here. Rows written before this field existed read
   * as not deleted.
   *
   * It hides, it does not erase, and the confirmation says so. The record keeps
   * its artwork — Restore that asks for the image again is not a restore — and
   * SDO authorization is per-schema, so a deleted row stays readable by every
   * org member. `PERMANENTLY_DELETE_WATERMARK_REQUEST`, offered in the deleted
   * view behind its own confirmation, is the action that actually removes it.
   */
  isDeleted?: boolean;
  /**
   * Email of the user who created the watermark, AS THE CLIENT REPORTED IT.
   *
   * Not provenance, and the Created By column should not be read as such: the
   * platform stores no author of its own for an SDO — `type StructuredData`
   * exposes no `createdBy` or `ownerId` — so there is nothing to corroborate
   * this against, and every field on the record is writable through the public
   * API by any org member. It is a convenience for "whose is this?", not an
   * accountability record. `stampWriteDefaults` preserves it across every later
   * write so it at least stays the FIRST claim rather than the most recent one.
   */
  createdBy?: string;
  /** Email of the user who last modified it. Same caveat as {@link createdBy}. */
  modifiedBy?: string;
}

/**
 * A definition ON ITS WAY TO BEING SAVED.
 *
 * Identical to the stored shape plus `sourceDataUrl`, the upload's own bytes.
 * That value is real but TRANSIENT: the form holds it between the file picker
 * and `provisionArtwork`, which uploads it to the watermark's TDO as the
 * `watermark-source-image` asset and drops it before the row is written.
 *
 * It has its own type rather than an optional key on the stored one so the
 * stored shape says only what is actually stored — a ~2.7MB field that the
 * schema does not declare and nothing persists has no business being
 * indistinguishable from one that is.
 */
export interface WatermarkDraft extends WatermarkDefinitionData {
  /** The upload's own bytes. Uploaded to the TDO, never written to the row. */
  sourceDataUrl?: string;
}

/** One watermark SDO record. */
export interface WatermarkDefinition {
  id: string;
  data: WatermarkDefinitionData;
  createdDateTime: string;
  modifiedDateTime: string;
}

/**
 * The record exactly as the server returned it, kept beside the vetted copy.
 *
 * `vetRecord` nulls anything that fails validation, which is right for RENDERING
 * and catastrophic for WRITING: an upsert replaces the whole document, so a
 * housekeeping write built from the vetted copy — flipping `enabled`, soft
 * deleting, restoring — would erase from the server whatever this client
 * happened to reject. A record over the size cap, a JPEG written by another
 * client, or anything a future build stores would be destroyed by a toggle.
 *
 * So writers read `raw` and only readers read `data`.
 */
export interface StoredWatermarkDefinition extends WatermarkDefinition {
  readonly raw: WatermarkDefinition;
}

export interface FetchWatermarksResponse {
  structuredDataObjects: {
    count: number;
    records: WatermarkDefinition[];
  };
}

export interface CreateWatermarkResponse {
  createStructuredData: WatermarkDefinition;
}

export interface DeleteWatermarkResponse {
  deleteStructuredData: { id: string };
}

/**
 * Snapshot a saved library definition into an applied instance (AC#3).
 *
 * The single implementation, deliberately: the editor panel and the folder-view
 * batch picker each had their own, and they disagreed — one copied a `scale` of
 * 0.6 and defaulted text to red, the other took the factory 0.4 and black. The
 * same library entry then landed at a different size depending on which surface
 * applied it, which makes "the document keeps its own copy" mean two things.
 *
 * `instanceId` is passed in rather than generated here so this stays pure and
 * testable; callers supply a uuid.
 */
/**
 * True when `name` is already worn by another LIVE watermark, case-insensitively.
 *
 * Names are how a watermark is chosen: the pickers show a name and, for image
 * marks, a thumbnail — and for text marks nothing else at all. Two rows called
 * "Confidential" are indistinguishable at the point of use, which is the point
 * where getting it wrong stamps the wrong thing onto a disclosure document.
 *
 * Soft-deleted rows do NOT hold their name. They are invisible on the settings
 * page, so refusing a name with no visible owner would be inexplicable — the
 * same call the Statuses page makes, and the reason restore has to check too:
 * deletion released the name and a live row may have taken it since.
 */
export function isWatermarkNameTaken(params: {
  readonly name: string;
  readonly watermarks: readonly WatermarkDefinition[];
  /** When editing or restoring, the row itself is not its own collision. */
  readonly excludeId?: string;
}): boolean {
  const normalized = params.name.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return params.watermarks.some(
    (w) =>
      w.data.isDeleted !== true &&
      w.id !== params.excludeId &&
      w.data.watermarkName.trim().toLowerCase() === normalized
  );
}

/** The media kinds a watermark can be applied to. */
export type WatermarkMediaKind = 'documents' | 'images' | 'videos';

/**
 * Whether a definition applies to one kind of media.
 *
 * THE reader, so the absent-key rule lives in one place. Absent means:
 *
 *  - documents → TRUE. Documents are the only implemented target, so a row that
 *    never stored the key has to keep working in the document editor. Narrowing
 *    an existing row on read is how the statuses page nearly made every old
 *    status vanish from pickers it already appeared in.
 *  - images / videos → FALSE. Neither is implemented, and a watermark offered
 *    for media nothing will stamp is worse than one not offered at all.
 */
export function watermarkAppliesTo(
  d: WatermarkDefinitionData,
  kind: WatermarkMediaKind
): boolean {
  if (kind === 'images') {
    return d.appliesToImages === true;
  }
  if (kind === 'videos') {
    return d.appliesToVideos === true;
  }
  return d.appliesToDocuments !== false;
}

export function appliedFromDefinition(
  instanceId: string,
  watermarkId: string,
  d: WatermarkDefinitionData
): AppliedWatermark {
  // The ONE reader of the definition's defaults. Everything seeded here becomes
  // the instance's own value and is never consulted on the definition again —
  // see the header for why that distinction is load-bearing.
  const seeded = {
    watermarkId,
    // Undefined is passed through deliberately: the factories fall back to
    // their own per-type default (0.3 text, 0.15 image), which is the right
    // answer for a row that never stored one now that the key is optional.
    ...(typeof d.opacity === 'number' ? { opacity: d.opacity } : {}),
    grayscale: d.grayscale === true,
    rotation: clampWatermarkRotation(d.rotation),
    // Placement defaults. `placement` stays `preset` — these say WHERE a preset
    // mark sits, and dragging it is still what makes it free.
    anchorH: d.anchorH ?? 'center',
    anchorV: d.anchorV ?? 'middle',
    inset: clampWatermarkInset(d.inset),
    // Absent reads as NOT bold, as it does everywhere else this flag is read
    // (`burnInWorker`, `helpers`, the overlay). It used to be the inverse here,
    // which made an old row's mark render bold in the preview and regular in the
    // burned-in PDF. New text is seeded bold by the drawer's own default, which
    // is where a default belongs — not in the reader of a stored value.
    bold: d.bold === true,
    align: d.align ?? 'center',
    ...(typeof d.customXPct === 'number' && typeof d.customYPct === 'number'
      ? { customXPct: d.customXPct, customYPct: d.customYPct }
      : {}),
  };
  return d.type === 'image'
    ? createImageWatermark(instanceId, {
        ...seeded,
        // No legacy fallback here any more: `vetRecord` folds the old shared
        // `size` into `imageScale` at the store boundary, so by the time a
        // definition reaches this function there is one field to read.
        scale: clampWatermarkScale(d.imageScale),
        // Seeded as the drawn form too, so a freshly applied watermark is
        // visible immediately rather than after a canvas round trip.
        //
        // ⚠️ It is the UNDERIVED base. Grayscale and tint are applied by
        // `deriveWatermarkImage`, which is async and cannot run from this
        // function. Every caller that puts the result on a document must derive
        // before dispatching ADD_WATERMARK, or a definition whose default is
        // grayscale arrives in colour: derivation otherwise runs only on
        // document open and on a CHANGE to those fields, and nothing has
        // changed — the operator's only route to the appearance they asked for
        // is toggling the switch off and on. Both callers do this
        // (`watchApply`, `watermarkAutoApply`); a third must too.
        imageDataUrl: d.imageBaseDataUrl ?? null,
        imageBaseDataUrl: d.imageBaseDataUrl ?? null,
        imageName: d.imageFileName ?? null,
        imageAspect: d.imageAspect ?? 1,
      })
    : createTextWatermark(instanceId, {
        ...seeded,
        // Sliced here as well as in `watermarksFromAssetBody`, and for the
        // same reason it is sliced there: a definition is org-writable through
        // the public API, and an oversized `text` is written into EVERY page's
        // content stream at burn-in and drawn as one nowrap line in the
        // overlay. It matters more on this path than on that one — auto-apply
        // reaches documents with no operator action.
        text: clampWatermarkText(d.text ?? '', MAX_WATERMARK_TEXT_LENGTH),
        color: d.colorHex || '#000000',
        // Likewise resolved at the boundary; this reads the one field.
        fontScale: fontScaleOf({ text: d.text ?? '', fontScale: d.fontScale }),
      });
}
